import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Activity, AlertTriangle, Briefcase, CheckCircle2, ChevronLeft, Gavel, Info, Loader2, Search, Sparkles, Users } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const LegalDataHubPage = () => {
  const [dataSources, setDataSources] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchState, setSearchState] = useState('ALL');
  const [jurisdiction, setJurisdiction] = useState('all');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSources, setLoadingSources] = useState(true);
  
  // Judge/Attorney lookup
  const [lookupType, setLookupType] = useState('judge');
  const [lookupName, setLookupName] = useState('');
  const [lookupState, setLookupState] = useState('CA');
  const [lookupResult, setLookupResult] = useState(null);
  
  const { trackEvent } = useEngagementTracker();
  
  useEffect(() => {
    trackEvent('legal-data', 'view');
    loadDataSources();
  }, [trackEvent]);
  
  const loadDataSources = async () => {
    try {
      const res = await axios.get(`${API}/legal-engine/status`);
      setDataSources(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSources(false);
    }
  };
  
  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      // Use the new unified search endpoint
      const res = await axios.post(`${API}/legal-engine/unified-search`, {
        query: searchQuery,
        jurisdiction: jurisdiction,
        include_historical: true,
        limit: 20
      });
      setSearchResults(res.data);
      trackEvent('legal-data', 'search', { jurisdiction });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const performLookup = async () => {
    if (!lookupName.trim()) return;
    setLoading(true);
    try {
      // Use comprehensive judge/attorney endpoint
      const endpoint = lookupType === 'judge' 
        ? '/legal-engine/judge-comprehensive' 
        : '/legal-data/attorney';
      const payload = lookupType === 'judge' 
        ? { judge_name: lookupName, court: lookupState }
        : { attorney_name: lookupName, state: lookupState };
      
      const res = await axios.post(`${API}${endpoint}`, payload);
      setLookupResult(res.data);
      trackEvent('legal-data', `${lookupType}_lookup`);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const STATE_OPTIONS = [
    { value: 'ALL', label: 'All States' },
    { value: 'CA', label: 'California' },
    { value: 'NY', label: 'New York' },
    { value: 'TX', label: 'Texas' },
    { value: 'FL', label: 'Florida' },
    { value: 'IL', label: 'Illinois' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'OH', label: 'Ohio' },
    { value: 'GA', label: 'Georgia' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'MI', label: 'Michigan' }
  ];
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="legal-data-hub-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Legal Data Hub</h1>
            <Badge className="bg-emerald-600">Premium</Badge>
          </div>
          <p className="text-muted-foreground">Unified access to Trellis Law, CourtListener, and court records across 46+ states</p>
        </div>
        
        {/* Data Sources Status */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Data Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSources ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {dataSources && Object.entries(dataSources).map(([key, source]) => (
                  <div key={key} className={`p-4 rounded-lg border ${source.configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {source.configured ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                      )}
                      <span className="font-semibold capitalize">{key.replace('_', ' ')}</span>
                      <Badge variant="outline" className="ml-auto">{source.type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{source.coverage}</p>
                    <div className="flex flex-wrap gap-1">
                      {source.features?.slice(0, 3).map((f, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Trellis Setup Instructions (if not configured) */}
        {dataSources && !dataSources.trellis?.configured && (
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Trellis Law Integration:</strong> For access to 2.5B+ state court records across 46 states, 
              contact <a href="https://support.trellis.law/trellis-api" target="_blank" rel="noopener noreferrer" className="underline">Trellis Sales</a> for 
              API access. Add your credentials to the system settings to enable premium features.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Case/Document Search */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Case & Document Search
              </CardTitle>
              <CardDescription>Search across federal and state court records</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Search Query</Label>
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="e.g., motion to dismiss, breach of contract..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>State</Label>
                  <Select value={searchState} onValueChange={setSearchState}>
                    <SelectTrigger><SelectValue placeholder="All States" /></SelectTrigger>
                    <SelectContent>
                      {STATE_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Jurisdiction</Label>
                  <Select value={jurisdiction} onValueChange={setJurisdiction}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courts</SelectItem>
                      <SelectItem value="federal">Federal Only</SelectItem>
                      <SelectItem value="state">State Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={performSearch} disabled={loading || !searchQuery.trim()} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Search Legal Records
              </Button>
              
              {searchResults && (
                <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{searchResults.total_results} results</span>
                    <div className="flex gap-1">
                      {searchResults.sources_searched?.map(s => (
                        <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Opinions */}
                  {searchResults.results?.opinions?.length > 0 && (
                    <div className="mb-3">
                      <h4 className="font-medium text-sm mb-2">Opinions ({searchResults.results.opinions.length})</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {searchResults.results.opinions.slice(0, 3).map((r, i) => (
                          <div key={i} className="p-2 bg-white rounded border text-sm">
                            <div className="font-medium">{r.caseName || 'Unknown Case'}</div>
                            <div className="text-muted-foreground text-xs flex gap-2">
                              <span>{r.dateFiled || 'N/A'}</span>
                              {r.citation_count > 0 && <span>• {r.citation_count} citations</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Dockets */}
                  {searchResults.results?.dockets?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">Dockets ({searchResults.results.dockets.length})</h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {searchResults.results.dockets.slice(0, 3).map((r, i) => (
                          <div key={i} className="p-2 bg-white rounded border text-sm">
                            <div className="font-medium">{r.caseName || r.case_name || 'Case'}</div>
                            <div className="text-muted-foreground text-xs">{r.court || 'Federal Court'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {searchResults.total_results === 0 && (
                    <p className="text-sm text-muted-foreground">No results found</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Judge/Attorney Lookup */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Judge & Attorney Intelligence
              </CardTitle>
              <CardDescription>Analytics and patterns (uses BAR references, no company names)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={lookupType === 'judge' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLookupType('judge')}
                >
                  <Gavel className="h-4 w-4 mr-1" /> Judge
                </Button>
                <Button
                  variant={lookupType === 'attorney' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLookupType('attorney')}
                >
                  <Briefcase className="h-4 w-4 mr-1" /> Attorney
                </Button>
              </div>
              <div>
                <Label>{lookupType === 'judge' ? 'Judge Name' : 'Attorney Name'}</Label>
                <Input
                  value={lookupName}
                  onChange={e => setLookupName(e.target.value)}
                  placeholder={lookupType === 'judge' ? 'e.g., Tanya Chutkan' : 'e.g., John Smith'}
                />
              </div>
              <div>
                <Label>State</Label>
                <Select value={lookupState} onValueChange={setLookupState}>
                  <SelectTrigger><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent>
                    {STATE_OPTIONS.filter(s => s.value).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={performLookup} disabled={loading || !lookupName.trim()} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Get {lookupType === 'judge' ? 'Judge' : 'Attorney'} Intelligence
              </Button>
              
              {lookupResult && (
                <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{lookupResult.judge_name || lookupResult.attorney_name}</span>
                    {lookupResult.bar_reference && (
                      <Badge variant="outline" className="text-xs">{lookupResult.bar_reference}</Badge>
                    )}
                  </div>
                  <div className="flex gap-1 mb-2">
                    {lookupResult.sources?.map(s => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                  {lookupResult.analytics && Object.keys(lookupResult.analytics).length > 0 ? (
                    <p className="text-sm text-green-600">Analytics data available from {Object.keys(lookupResult.analytics).join(', ')}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Configure Trellis for detailed analytics</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Premium Features */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Premium Features (Requires Trellis API)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">AI Case Strategy</h4>
                <p className="text-sm text-muted-foreground">Get AI-powered strategy reports with risk analysis, timelines, and similar case outcomes.</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">Motion Outcome Prediction</h4>
                <p className="text-sm text-muted-foreground">Historical motion success rates by judge, case type, and jurisdiction.</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold mb-2">AI Motion Drafting</h4>
                <p className="text-sm text-muted-foreground">Generate motion drafts based on similar successful cases.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </main>
  );
};


export default LegalDataHubPage;
